import React, { useMemo } from 'react';
import { BoxProps, Flex, Text, useColorModeValue } from '@chakra-ui/react';

import { createCustomWifiIcon } from 'clipsal-cortex-icons/src/custom-dynamic-icons';

import { LockIcon } from '../../styles/custom-icons';

type Props = {
  ssid: string;
  signalStrength: number; // Expected to be a value between 0 and 100, higher values indicate stronger signal
  isSelected?: boolean;
  isDisabled?: boolean;
  onClick: (index: number) => void;
} & BoxProps;

/**
 * Displays a Wi-Fi network in a list, displays SSID and signal strength.
 */
export function WifiNetworkListItem({ ssid, signalStrength, onClick, isSelected, isDisabled, ...boxProps }: Props) {
  const iconColor = useColorModeValue('customGrey.500', 'customGrey.200');
  const signalColor = useColorModeValue('customGrey.400', 'customGrey.200');
  const selectedColor = useColorModeValue('customGrey.300', 'customGrey.600');

  const SignalIcon = useMemo(() => {
    if (signalStrength >= 70) {
      return createCustomWifiIcon(4, '#3DCD58');
    } else if (signalStrength >= 40) {
      return createCustomWifiIcon(3, '#3DCD58');
    } else if (signalStrength >= 20) {
      return createCustomWifiIcon(2, '#3DCD58');
    } else {
      return createCustomWifiIcon(1, '#3DCD58');
    }
  }, [signalStrength]);

  return (
    <Flex
      onClick={isDisabled ? undefined : onClick}
      h="72px"
      px={3}
      align="center"
      bg={isSelected ? selectedColor : 'transparent'}
      cursor={isDisabled ? 'default' : 'pointer'}
      opacity={isDisabled ? 0.5 : 1}
      justify="space-between"
      borderBottom="1px solid"
      borderColor={selectedColor}
      {...boxProps}
    >
      <Flex align="center" minW={0} flex="1">
        <SignalIcon boxSize={7} color={signalColor} />
        <Text
          ml={4}
          noOfLines={2} // optional, limits height visually
          wordBreak="break-word"
          whiteSpace="normal"
          overflowWrap="anywhere"
          textAlign="left"
        >
          {ssid}
        </Text>
      </Flex>
      {/* TODO: Test with hardware to know which data points we can use to show lock/unlock icon to add as prop */}
      <LockIcon ml={2} mr={4} boxSize={6} color={iconColor} />
    </Flex>
  );
}
